/**
 * ReservationEditorPanel
 *
 * Self-contained reservation create/edit form designed to be embedded inline —
 * in a secondary drawer panel, a page section, or anywhere else in the flow.
 * Intentionally has NO modal shell wrapper. Save / Cancel are inline buttons.
 *
 * Used by:
 *  - InvoiceDrawer secondary panel (create + link flow)
 *  - ReservationEditorModal (wraps this in AdminModalShell for the full-page editor)
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import AccountTypeahead, { AccountTypeaheadOption } from '../account/AccountTypeahead';
import CustomerTypeahead, { CustomerTypeaheadOption } from '../customer/CustomerTypeahead';
import LoadingSpinner from '../LoadingSpinner';
import { adminAPI } from '../../services/api';

// ─── Types ──────────────────────────────────────────────────────────────────

export type ReservationEditorInitialValues = {
  reservationRef?: string | null;
  account?: { id?: string | null; name?: string | null } | null;
  customer?: { id?: string | null; name?: string | null; email?: string | null } | null;
  customerCompanyName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

type ReservationDetail = {
  id: string;
  reservationRef?: string | null;
  sourceType?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  totalPrice?: string | number | null;
  depositAmount?: string | number | null;
  customerReference?: string | null;
  customer?: { id?: string | null; email?: string | null; name?: string | null } | null;
  account?: { id?: string | null; name?: string | null } | null;
  tripDetails?: {
    passenger_name?: string | null;
    customer_name?: string | null;
    customer_email?: string | null;
    customer_company_name?: string | null;
    pickup_time?: string | null;
    pickup_location?: string | null;
    dropoff_location?: string | null;
    vehicle_label?: string | null;
    vehicle_type?: string | null;
    driver_name?: string | null;
    payment_method?: string | null;
    affiliate?: string | null;
  } | null;
};

type FormState = {
  reservationRef: string;
  sourceType: string;
  status: string;
  startDate: string;
  endDate: string;
  totalPrice: string;
  depositAmount: string;
  accountId: string | null;
  customerReference: string | null;
  customerName: string;
  customerEmail: string;
  customerCompanyName: string;
  passengerName: string;
  pickupTime: string;
  pickupLocation: string;
  dropoffLocation: string;
  vehicleLabel: string;
  vehicleType: string;
  driverName: string;
  paymentMethod: string;
  affiliate: string;
  changeReason: string;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function toLocalDateTimeInput(value?: string | null) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIsoString(value: string) {
  if (!value.trim()) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

function defaultDateRange() {
  const start = new Date();
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return { startDate: toLocalDateTimeInput(start.toISOString()), endDate: toLocalDateTimeInput(end.toISOString()) };
}

function createEmpty(): FormState {
  const { startDate, endDate } = defaultDateRange();
  return {
    reservationRef: '', sourceType: 'manual', status: 'pending', startDate, endDate,
    totalPrice: '0.00', depositAmount: '0.00', accountId: null, customerReference: null,
    customerName: '', customerEmail: '', customerCompanyName: '', passengerName: '',
    pickupTime: '', pickupLocation: '', dropoffLocation: '', vehicleLabel: '',
    vehicleType: '', driverName: '', paymentMethod: '', affiliate: '', changeReason: '',
  };
}

function fromDetail(r: ReservationDetail): FormState {
  return {
    reservationRef: r.reservationRef || '',
    sourceType: r.sourceType || 'manual',
    status: r.status || 'pending',
    startDate: toLocalDateTimeInput(r.startDate),
    endDate: toLocalDateTimeInput(r.endDate),
    totalPrice: String(r.totalPrice ?? '0.00'),
    depositAmount: String(r.depositAmount ?? '0.00'),
    accountId: r.account?.id || null,
    customerReference: r.customerReference || r.customer?.id || null,
    customerName: r.tripDetails?.customer_name || r.customer?.name || '',
    customerEmail: r.tripDetails?.customer_email || r.customer?.email || '',
    customerCompanyName: r.tripDetails?.customer_company_name || r.account?.name || '',
    passengerName: r.tripDetails?.passenger_name || '',
    pickupTime: r.tripDetails?.pickup_time || '',
    pickupLocation: r.tripDetails?.pickup_location || '',
    dropoffLocation: r.tripDetails?.dropoff_location || '',
    vehicleLabel: r.tripDetails?.vehicle_label || '',
    vehicleType: r.tripDetails?.vehicle_type || '',
    driverName: r.tripDetails?.driver_name || '',
    paymentMethod: r.tripDetails?.payment_method || '',
    affiliate: r.tripDetails?.affiliate || '',
    changeReason: '',
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

interface ReservationEditorPanelProps {
  /** null / undefined → create mode; non-null string → edit mode */
  reservationId?: string | null;
  /** Pre-populate create form with context from the calling surface */
  initialValues?: ReservationEditorInitialValues;
  onSaved?: (reservation: any) => void;
  onCancel?: () => void;
  /** Extra class on the root div — useful for panel vs modal layout tweaks */
  className?: string;
  /** When true, shows a compact section header instead of verbose labels */
  compact?: boolean;
}

export default function ReservationEditorPanel({
  reservationId,
  initialValues,
  onSaved,
  onCancel,
  className = '',
  compact = false,
}: ReservationEditorPanelProps) {
  const queryClient = useQueryClient();
  const isEditing = Boolean(reservationId);

  const [form, setForm] = useState<FormState>(createEmpty());
  const [selectedAccount, setSelectedAccount] = useState<AccountTypeaheadOption | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTypeaheadOption | null>(null);
  const [showTripDetails, setShowTripDetails] = useState(false);

  const set = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  // ── Fetch for edit mode ─────────────────────────────────────────────────
  const { data: reservationData, isLoading: loadingReservation } = useQuery<ReservationDetail | null>(
    ['reservation-editor-panel', reservationId],
    async () => {
      const res = await adminAPI.getReservation(reservationId!);
      return res.data;
    },
    { enabled: isEditing && Boolean(reservationId), staleTime: 60_000, retry: false },
  );

  // ── Initialise form ─────────────────────────────────────────────────────
  useEffect(() => {
    if (isEditing) {
      if (reservationData) {
        setForm(fromDetail(reservationData));
        setSelectedAccount(
          reservationData.account?.id
            ? { id: reservationData.account.id, label: reservationData.account.name || 'Account', description: '' }
            : null,
        );
        setSelectedCustomer(
          reservationData.customer?.id || reservationData.customer?.email
            ? {
                id: reservationData.customer?.id || reservationData.customer?.email || 'customer',
                label: reservationData.customer?.name || reservationData.customer?.email || 'Customer',
                description: reservationData.customer?.email || '',
                email: reservationData.customer?.email || '',
              }
            : null,
        );
      }
    } else {
      const base = createEmpty();
      setForm({
        ...base,
        reservationRef: initialValues?.reservationRef || '',
        accountId: initialValues?.account?.id || null,
        customerReference: initialValues?.customer?.id || null,
        customerName: initialValues?.customer?.name || '',
        customerEmail: initialValues?.customer?.email || '',
        customerCompanyName: initialValues?.customerCompanyName || initialValues?.account?.name || '',
        startDate: initialValues?.startDate ? toLocalDateTimeInput(initialValues.startDate) : base.startDate,
        endDate: initialValues?.endDate ? toLocalDateTimeInput(initialValues.endDate) : base.endDate,
      });
      setSelectedAccount(
        initialValues?.account?.id
          ? { id: initialValues.account.id, label: initialValues.account.name || 'Account', description: '' }
          : null,
      );
      setSelectedCustomer(
        initialValues?.customer?.id || initialValues?.customer?.email
          ? {
              id: initialValues?.customer?.id || initialValues?.customer?.email || '',
              label: initialValues?.customer?.name || initialValues?.customer?.email || 'Customer',
              description: initialValues?.customer?.email || '',
              email: initialValues?.customer?.email || '',
            }
          : null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, reservationId, reservationData, initialValues?.account?.id, initialValues?.customer?.id]);

  // ── Save ────────────────────────────────────────────────────────────────
  const saveMutation = useMutation(
    async () => {
      const startDate = toIsoString(form.startDate);
      const endDate = toIsoString(form.endDate);
      if (!startDate || !endDate) throw new Error('Start and end date are required');

      const payload = {
        reservationRef: form.reservationRef || null,
        sourceType: form.sourceType || 'manual',
        status: form.status || 'pending',
        startDate,
        endDate,
        totalPrice: form.totalPrice || '0.00',
        depositAmount: form.depositAmount || '0.00',
        accountId: form.accountId,
        customerReference: form.customerReference,
        customerName: form.customerName || null,
        customerEmail: form.customerEmail || null,
        customerCompanyName: form.customerCompanyName || null,
        passengerName: form.passengerName || null,
        pickupTime: form.pickupTime || null,
        pickupLocation: form.pickupLocation || null,
        dropoffLocation: form.dropoffLocation || null,
        vehicleLabel: form.vehicleLabel || null,
        vehicleType: form.vehicleType || null,
        driverName: form.driverName || null,
        paymentMethod: form.paymentMethod || null,
        affiliate: form.affiliate || null,
        changeReason: isEditing
          ? form.changeReason.trim() || 'Updated reservation from editor'
          : null,
      };

      const response = isEditing && reservationId
        ? await adminAPI.updateReservation(reservationId, payload)
        : await adminAPI.createReservation(payload);
      return response.data;
    },
    {
      onSuccess: async (reservation) => {
        toast.success(isEditing ? 'Reservation updated' : 'Reservation created');
        await Promise.all([
          queryClient.invalidateQueries('admin-reservations'),
          reservationId ? queryClient.invalidateQueries(['admin-reservation', reservationId]) : Promise.resolve(),
          queryClient.invalidateQueries(['admin-reservation', reservation?.id]),
        ]);
        onSaved?.(reservation);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to save reservation');
      },
    },
  );

  // ── Render ──────────────────────────────────────────────────────────────

  if (isEditing && loadingReservation) {
    return <LoadingSpinner className="py-12" />;
  }

  const labelCls = `mb-1 block text-xs font-medium text-on-surface-variant`;
  const sectionHeader = (text: string) => (
    <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{text}</p>
  );

  return (
    <div className={`space-y-5 ${className}`}>
      {/* ── Core fields ─────────────────────────────── */}
      <div className="space-y-3">
        {sectionHeader('Core details')}
        <div>
          <label className={labelCls}>Reservation reference</label>
          <input
            type="text"
            className="input-field"
            value={form.reservationRef}
            onChange={(e) => set({ reservationRef: e.target.value })}
            placeholder="RES-2026-0042"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Start</label>
            <input
              type="datetime-local"
              className="input-field"
              value={form.startDate}
              onChange={(e) => set({ startDate: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>End</label>
            <input
              type="datetime-local"
              className="input-field"
              value={form.endDate}
              onChange={(e) => set({ endDate: e.target.value })}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Status</label>
            <select className="input-field" value={form.status} onChange={(e) => set({ status: e.target.value })}>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Source</label>
            <select className="input-field" value={form.sourceType} onChange={(e) => set({ sourceType: e.target.value })}>
              <option value="manual">Manual</option>
              <option value="import">Import</option>
              <option value="booking">Booking</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Total ($)</label>
            <input type="number" min="0" step="0.01" className="input-field" value={form.totalPrice} onChange={(e) => set({ totalPrice: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Deposit ($)</label>
            <input type="number" min="0" step="0.01" className="input-field" value={form.depositAmount} onChange={(e) => set({ depositAmount: e.target.value })} />
          </div>
        </div>
        {isEditing ? (
          <div>
            <label className={labelCls}>Change reason</label>
            <textarea
              className="input-field min-h-[84px]"
              value={form.changeReason}
              onChange={(e) => set({ changeReason: e.target.value })}
              placeholder="Reason for changing this reservation"
            />
          </div>
        ) : null}
      </div>

      {/* ── Customer ─────────────────────────────────── */}
      <div className="space-y-3 rounded-card border border-ghost p-3">
        <div className="flex items-center justify-between gap-2">
          {sectionHeader('Customer')}
          {(form.customerReference || form.customerEmail || form.customerName) && (
            <button
              type="button"
              className="text-xs text-on-surface-variant hover:text-error"
              onClick={() => {
                setSelectedCustomer(null);
                set({ customerReference: null, customerName: '', customerEmail: '' });
              }}
            >
              Clear
            </button>
          )}
        </div>
        <CustomerTypeahead
          value={selectedCustomer}
          onChange={(v) => {
            setSelectedCustomer(v);
            set({ customerReference: v?.id || null, customerName: v?.label || form.customerName, customerEmail: v?.email || form.customerEmail });
          }}
          placeholder="Search by name or email…"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Name</label>
            <input type="text" className="input-field" value={form.customerName} onChange={(e) => set({ customerName: e.target.value })} placeholder="Customer name" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" className="input-field" value={form.customerEmail} onChange={(e) => set({ customerEmail: e.target.value })} placeholder="customer@example.com" />
          </div>
        </div>
      </div>

      {/* ── Account ──────────────────────────────────── */}
      <div className="space-y-3 rounded-card border border-ghost p-3">
        <div className="flex items-center justify-between gap-2">
          {sectionHeader('Account')}
          {form.accountId && (
            <button
              type="button"
              className="text-xs text-on-surface-variant hover:text-error"
              onClick={() => {
                setSelectedAccount(null);
                set({ accountId: null, customerCompanyName: '' });
              }}
            >
              Clear
            </button>
          )}
        </div>
        <AccountTypeahead
          value={selectedAccount}
          onChange={(v) => {
            setSelectedAccount(v);
            set({ accountId: v?.id || null, customerCompanyName: v?.label || form.customerCompanyName });
          }}
          placeholder="Search account by name…"
        />
        <div>
          <label className={labelCls}>Company snapshot</label>
          <input type="text" className="input-field" value={form.customerCompanyName} onChange={(e) => set({ customerCompanyName: e.target.value })} placeholder="Company name" />
        </div>
      </div>

      {/* ── Trip details (collapsible) ────────────────── */}
      <div>
        <button
          type="button"
          className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-on-surface-variant"
          onClick={() => setShowTripDetails((v) => !v)}
        >
          <span>Trip details</span>
          <span>{showTripDetails ? '▲' : '▼'}</span>
        </button>
        {showTripDetails && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            {(
              [
                ['Passenger', 'passengerName', 'Passenger or rider'],
                ['Pickup', 'pickupLocation', 'Pickup location'],
                ['Dropoff', 'dropoffLocation', 'Dropoff location'],
                ['Vehicle label', 'vehicleLabel', 'Sprinter, SUV…'],
                ['Vehicle type', 'vehicleType', ''],
                ['Driver', 'driverName', ''],
                ['Affiliate', 'affiliate', ''],
                ['Payment method', 'paymentMethod', 'Corporate card, ACH…'],
              ] as [string, keyof FormState, string][]
            ).map(([label, field, placeholder]) => (
              <div key={field} className="col-span-1">
                <label className={labelCls}>{label}</label>
                <input
                  type="text"
                  className="input-field"
                  value={form[field] as string}
                  onChange={(e) => set({ [field]: e.target.value })}
                  placeholder={placeholder}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Actions ──────────────────────────────────── */}
      <div className="flex gap-3 border-t border-ghost pt-4">
        {onCancel && (
          <button type="button" className="btn-secondary flex-1" onClick={onCancel} disabled={saveMutation.isLoading}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="btn-primary flex-1"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isLoading}
        >
          {saveMutation.isLoading ? 'Saving…' : isEditing ? 'Save changes' : 'Create reservation'}
        </button>
      </div>
    </div>
  );
}
